import { Box, Typography } from "@mui/material";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import StyledVideo from "components/Styled/Video";
import {
  CAMPAIGNS_PREVIEW,
  HOW_TO_USE_CAMPAIGN_VIDEO_LINK,
} from "constants/influencer";

const STEPS = [
  {
    icon: "fa-regular fa-bullseye-arrow",
    step: "1",
    title: "Select Brands",
    description: "Choose brands from your list or find new ones",
    gradient: "linear-gradient(135deg, #EFF6FF 0%, #EEF2FF 100%)",
    border: "#BFDBFE",
    iconColor: "#3B82F6",
    stepColor: "#1D4ED8",
  },
  {
    icon: "fa-regular fa-envelope",
    step: "2",
    title: "Craft Emails",
    description: "Use templates or create from scratch",
    gradient: "linear-gradient(135deg, #F5F3FF 0%, #FDF4FF 100%)",
    border: "#DDD6FE",
    iconColor: "#8B5CF6",
    stepColor: "#6D28D9",
  },
  {
    icon: "fa-regular fa-arrow-trend-up",
    step: "3",
    title: "Track Results",
    description: "Monitor opens, responses, and conversions",
    gradient: "linear-gradient(135deg, #ECFDF5 0%, #F0FDFA 100%)",
    border: "#A7F3D0",
    iconColor: "#10B981",
    stepColor: "#065F46",
  },
];

interface Props {
  onCreateCampaign: () => void;
}

const NoCampaignsYet = ({ onCreateCampaign }: Props) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        py: { xs: 6, md: 3 },
        px: { xs: 2, md: 4 },
        maxWidth: 680,
        mx: "auto",
      }}
    >
      {/* Video thumbnail */}
      <Box sx={{ mb: 3 }}>
        <StyledVideo
          image={CAMPAIGNS_PREVIEW}
          videoPreview={CAMPAIGNS_PREVIEW}
          link={HOW_TO_USE_CAMPAIGN_VIDEO_LINK}
          title="How to Use Campaigns"
          isSmall
        />
      </Box>

      {/* Title */}
      <Typography
        variant="h4"
        textAlign={"center"}
        sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: 24, md: 30 } }}
      >
        Launch Your First Campaign
      </Typography>

      {/* Subtitle */}
      <Typography
        textAlign={"center"}
        sx={{
          color: "text.secondary",
          fontSize: { xs: 14, md: 15 },
          lineHeight: 1.7,
          maxWidth: 520,
          mb: 5,
        }}
      >
        Campaigns help you organize brand outreach at scale. Send personalized
        emails to multiple brands, track opens and responses, and manage
        follow-ups—all in one place.
      </Typography>

      {/* Step cards */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          width: "100%",
          mb: 5,
        }}
      >
        {STEPS.map(
          ({
            icon,
            step,
            title,
            description,
            gradient,
            border,
            iconColor,
            stepColor,
          }) => (
            <Box
              key={step}
              sx={{
                flex: 1,
                background: gradient,
                border: `1.5px solid ${border}`,
                borderRadius: 3,
                p: 3,
                minWidth: 250,
                textAlign: "left",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 1px 4px rgba(0,0,0,0.08)`,
                  mb: 1,
                }}
              >
                <Box
                  component="i"
                  className={icon}
                  sx={{ fontSize: 16, color: iconColor }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: stepColor,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Step {step}
              </Typography>
              <Typography
                sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.3 }}
              >
                {title}
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.5 }}
              >
                {description}
              </Typography>
            </Box>
          ),
        )}
      </Box>

      {/* CTA */}
      <StyledLargeButton
        size="large"
        onClick={onCreateCampaign}
        styles={{
          textTransform: "none",
          fontWeight: 700,
          fontSize: 16,
          px: 4,
          py: 1.5,
          borderRadius: 2.5,
          background: "#009966",
          boxShadow:
            "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)",
          "&:hover": {
            background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
            boxShadow: "0 6px 20px rgba(16,185,129,0.45)",
          },
        }}
      >
        Create Your First Campaign
      </StyledLargeButton>
      <Typography sx={{ mt: 1.5, fontSize: 12, color: "text.disabled" }}>
        Takes less than 5 minutes to set up
      </Typography>
    </Box>
  );
};

export default NoCampaignsYet;
